import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { AdminShell } from "@/components/AdminShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { adminAuditLog } from "@/lib/orders-admin.functions";
import { statusLabel, shipmentStatusLabel, kr } from "@/lib/orders";

export const Route = createFileRoute("/_authenticated/admin/revisionslogg")({
  component: AdminAuditLog,
  head: () => ({
    meta: [
      { title: "Revisionslogg – administration" },
      { name: "robots", content: "noindex" },
    ],
  }),
});

const KINDS = [
  { value: "all", label: "Allt" },
  { value: "order", label: "Order" },
  { value: "shipment", label: "Frakt" },
  { value: "dispute", label: "Tvist" },
  { value: "decision", label: "Beslut" },
] as const;

const kindTone: Record<string, string> = {
  order: "bg-secondary text-secondary-foreground",
  shipment: "bg-blue-100 text-blue-800",
  dispute: "bg-amber-100 text-amber-900",
  decision: "bg-emerald-100 text-emerald-900",
};

const actorLabel = (role: string): string =>
  ({
    buyer: "Köpare",
    seller: "Säljare",
    admin: "Administratör",
    system: "Systemet",
    simulation: "Simulering",
  })[role] ?? role;

function actionLabel(kind: string, action: string): string {
  if (kind === "shipment") return shipmentStatusLabel(action);
  if (action.startsWith("beslut_"))
    return (
      {
        beslut_release: "Beslut: pengar frisläpps till säljare",
        beslut_refund_full: "Beslut: full återbetalning till köpare",
        beslut_refund_partial: "Beslut: delvis återbetalning",
        beslut_reject: "Beslut: tvisten avslås",
      }[action] ?? `Beslut: ${action.replace("beslut_", "")}`
    );
  if (action.startsWith("steg_")) return `Ärendesteg: ${action.replace("steg_", "")}`;
  if (action === "dispute_opened") return "Tvist anmäld";
  if (action === "dispute_withdrawn") return "Tvist tillbakadragen";
  return statusLabel(action);
}

const when = (iso: string): string =>
  new Date(iso).toLocaleString("sv-SE", { dateStyle: "short", timeStyle: "short" });

function AdminAuditLog() {
  const load = useServerFn(adminAuditLog);
  const [kind, setKind] = useState<(typeof KINDS)[number]["value"]>("all");
  const [term, setTerm] = useState("");
  const [search, setSearch] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["admin-audit", kind, search],
    queryFn: () => load({ data: { kind, search: search || undefined, limit: 300 } }),
    refetchInterval: 60_000,
  });

  const rows = (data?.rows ?? []) as any[];

  return (
    <AdminShell title="Revisionslogg">
      <p className="mt-2 text-muted-foreground">
        Varje steg och beslut i Trygg affär – betalning, frakt, tvist, frisläppning, återbetalning
        och manuella åtgärder – med tidpunkt och vem som gjorde det.
      </p>

      <form
        className="mt-4 flex flex-wrap items-center gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          setSearch(term.trim());
        }}
      >
        <Input
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          placeholder="Sök ordernummer, annons eller spårningsnummer"
          className="w-full max-w-sm"
        />
        <Button type="submit" variant="secondary">
          Sök
        </Button>
        <div className="flex flex-wrap gap-2">
          {KINDS.map((k) => (
            <Button
              key={k.value}
              type="button"
              size="sm"
              variant={kind === k.value ? "default" : "outline"}
              onClick={() => setKind(k.value)}
            >
              {k.label}
            </Button>
          ))}
        </div>
      </form>

      {isLoading ? (
        <p className="mt-8 text-muted-foreground">Laddar logg…</p>
      ) : rows.length === 0 ? (
        <p className="mt-8 text-muted-foreground">Inga händelser matchar filtret.</p>
      ) : (
        <div className="mt-6 overflow-x-auto rounded-lg border border-border">
          <table className="w-full min-w-[820px] text-sm">
            <thead className="bg-muted/60 text-left text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-3 py-2">Tidpunkt</th>
                <th className="px-3 py-2">Order</th>
                <th className="px-3 py-2">Typ</th>
                <th className="px-3 py-2">Händelse</th>
                <th className="px-3 py-2">Utförd av</th>
                <th className="px-3 py-2">Belopp</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-border align-top">
                  <td className="whitespace-nowrap px-3 py-2 tabular-nums text-muted-foreground">
                    {when(r.at)}
                  </td>
                  <td className="px-3 py-2">
                    <Link
                      to="/admin/ordrar/$id"
                      params={{ id: r.orderId }}
                      className="font-medium underline-offset-2 hover:underline"
                    >
                      {r.orderNo ?? r.orderId.slice(0, 8)}
                    </Link>
                    <p className="text-xs text-muted-foreground">{r.adTitle}</p>
                    {r.simulated ? (
                      <span className="mt-1 inline-block rounded bg-muted px-1.5 py-0.5 text-[11px] text-muted-foreground">
                        Simulerad
                      </span>
                    ) : null}
                  </td>
                  <td className="px-3 py-2">
                    <span
                      className={`rounded px-2 py-0.5 text-xs font-medium ${kindTone[r.kind] ?? "bg-secondary"}`}
                    >
                      {KINDS.find((k) => k.value === r.kind)?.label ?? r.kind}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <p className="font-medium">{actionLabel(r.kind, r.action)}</p>
                    {r.detail ? (
                      <p className="text-xs text-muted-foreground">{r.detail}</p>
                    ) : null}
                  </td>
                  <td className="px-3 py-2">
                    <p>{actorLabel(r.actorRole)}</p>
                    {r.actorName ? (
                      <p className="text-xs text-muted-foreground">{r.actorName}</p>
                    ) : null}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 tabular-nums">
                    {r.amount ? kr(r.amount) : "–"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AdminShell>
  );
}
